/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-undef */
import 'amazon-connect-streams';
import {
  API_PATIENTS_COMMENTS,
  API_READINGS_ACTIONS,
  API_CCM_PATIENT_CALL,
} from 'services/dataService/resources';
import { showResult } from 'infrastructure/hooks/utils';
import { SocketRoutes } from 'services/wsService';
import { differenceInSeconds } from 'date-fns';
import {
  emitCallConnectingEvent,
  emitCallEndedEvent,
  emitCallStartedEvent,
} from 'infrastructure/customEvents/callEvents';
import { ModuleType } from 'infrastructure/enums';

import dataService from '../dataService';

import type { TActionFrom } from 'infrastructure/interfaces/common';
import type WS from 'services/wsService';
import type { Orchestration } from 'aws-rum-web/dist/cjs/orchestration/Orchestration';

const AWS_CONNECT_URL_CCP = process.env.REACT_APP_CONNECT_URL_CCP!;
const AWS_CONNECT_URL = process.env.REACT_APP_CONNECT_URL!;

interface ConnectDetails {
  patientGuid: string;
  connectedAt?: Date;
  /** When this tab saw the contact start ringing. Used to decide whether the
   *  agent's error state belongs to THIS call — see the failure reason in
   *  onEnded, where a leftover state from an earlier dial must not be blamed
   *  on this one. */
  ringStartedAt: Date;
}

const contactToPatientMap: Map<string, ConnectDetails> = new Map();

interface ConnectCallbacks {
  onCallStarted?: (
    callId: string,
    patientGuid: string,
    isRpmCall: boolean,
  ) => void;
  onCallEnded?: () => void;
}

interface IInitProps {
  phoneNumber: string;
  patientGuid: string;
  setCallId: (callId: string) => void;
  isRpmCall: boolean;
  deviceId: string;
  readingId: string;
  wsClient: typeof WS;
  awsRumAgent: Orchestration | null;
  isReadOnlyRole: boolean;
  actionFrom?: TActionFrom;
  callbacks?: ConnectCallbacks;
}

// Shared mutable props read by the single registered contact handler.
// Updated on every init() so the handler always registers calls under the
// patient whose chart is currently open — not a value frozen at registration.
let currentProps: IInitProps | null = null;
// Handle to the one live connect.contact() subscription, so terminate() can
// remove it. Registering more than one handler makes each fire onConnecting
// with its own patientGuid, spreading a single call across multiple patients.
let contactSubscription: connect.Subscription | null = null;
let ccpInitialized = false;
// The one live Agent handle. connect.agent(f) PERMANENTLY subscribes f to the
// event bus and never invokes it before agent init, so calling it per snapshot
// (the panel polls every second) both leaks a subscription per call and defers
// the callback to a later, staler moment when the agent is not up yet. One
// subscription caches the instance; everything else reads it synchronously.
let agentSubscription: connect.Subscription | null = null;
let cachedAgent: connect.Agent | null = null;

// ── Cross-tab call ownership ───────────────────────────────────────────────
// Amazon Connect runs ONE shared worker that syncs every contact to every open
// tab, so onConnecting fires in tabs that did NOT place the call. The SDK gives
// no "which tab dialed" signal — softphone dials happen inside the CCP iframe,
// which our outer window cannot observe. To register each call under exactly
// one patient we latch ownership at the earliest moment we do see the contact
// (onConnecting): the active/focused tab claims the contactId, broadcasts the
// claim to the other tabs, and that claim is frozen — later focus changes or a
// slower tab cannot re-register it. This works for repeat softphone calls in an
// already-open popup (no dependence on init() re-running) and prevents the
// duplicate cross-patient rows we saw with multiple open popups.
const CALL_OWNERSHIP_CHANNEL = 'connect-call-ownership';
let ownershipChannel: BroadcastChannel | null = null;
const contactsClaimedByOtherTab = new Set<string>();
// Set when THIS tab dials via agent.connect (below), consumed by the first
// onConnecting. The first call on a fresh CCP triggers an Amazon Connect login
// popup that steals focus, so document.hasFocus() is false when that call
// connects. This flag lets the dialing tab claim its own call regardless of
// focus. Repeat softphone redials don't go through agent.connect, so those
// still rely on focus — hence ownership is claimed on EITHER signal.
let hasPendingOutboundDial = false;
// Set when the agent hangs up through this app's own controls (End call /
// Cancel call / closing the popup), cleared on each onConnecting. onEnded uses
// it to tell "the agent abandoned the ring" apart from "the ring died on its
// own" — only the latter is reported to the panel as a call that could not
// connect.
let agentEndedCall = false;

const getOwnershipChannel = (): BroadcastChannel => {
  if (!ownershipChannel) {
    ownershipChannel = new BroadcastChannel(CALL_OWNERSHIP_CHANNEL);
    ownershipChannel.onmessage = (e: MessageEvent<{ contactId?: string }>) => {
      if (e.data?.contactId) contactsClaimedByOtherTab.add(e.data.contactId);
    };
  }
  return ownershipChannel;
};

// Returns true if THIS tab should register the given contact. A tab owns the
// call if it dialed it (pending flag — survives the auth-redirect focus loss)
// OR it is the focused tab (covers softphone redials that skip agent.connect).
// The first claim wins and is broadcast so no other tab double-registers.
const claimCallOwnership = (contactId: string): boolean => {
  const channel = getOwnershipChannel();
  if (contactsClaimedByOtherTab.has(contactId)) return false;

  const dialedHere = hasPendingOutboundDial;
  hasPendingOutboundDial = false;
  // document.hasFocus() is false in every background tab, so only the tab the
  // nurse is looking at passes on focus. The dial flag covers the case where
  // the login popup stole focus from the originating tab on the first call.
  const focusedHere = typeof document !== 'undefined' && document.hasFocus();
  if (!dialedHere && !focusedHere) return false;

  channel.postMessage({ contactId });
  return true;
};

const init = (props: IInitProps) => {
  const { phoneNumber } = props;
  currentProps = props;

  const containerDiv = document.getElementById('connect-el')!;
  ccpInitialized = true;
  connect.core.initCCP(containerDiv, {
    ccpUrl: AWS_CONNECT_URL_CCP,
    loginUrl: AWS_CONNECT_URL,
    loginPopup: true,
    loginPopupAutoClose: true,
    region: 'us-east-1',
    pageOptions: {
      enableAudioDeviceSettings: false,
      enablePhoneTypeSettings: false,
    },
    softphone: {
      allowFramedSoftphone: true,
    },
  });

  if (!agentSubscription) {
    agentSubscription = connect.agent((agent) => {
      cachedAgent = agent;
    });
  }

  if (phoneNumber) {
    connect.agent((agent) => {
      const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber);
      // Mark this tab as the dialer so it can claim the resulting call even if
      // the Amazon Connect login popup steals focus before the call connects.
      hasPendingOutboundDial = true;
      agent.connect(endpoint);
    });

    // Register the contact handler exactly once per CCP session. The handler
    // reads currentProps at event time (not a frozen closure), so every call
    // registers under whichever patient chart is open when it connects.
    // terminate() unsubscribes this handle; without that, reopening the modal
    // would stack handlers, each firing with its own stale patientGuid.
    if (!contactSubscription) {
      contactSubscription = connect.contact((contact) => {
        contact.onConnecting(async (event) => {
          if (event.isInbound()) return;

          const callProps = currentProps;
          if (!callProps || !callProps.patientGuid) return;

          const { contactId } = event;

          // Only the active tab (the one the nurse dialed from) registers this
          // call. Every other open popup receives the same event via the shared
          // worker and must skip it, or the call lands on the wrong patient.
          if (!claimCallOwnership(contactId)) return;

          // A fresh ring: nothing has been hung up yet.
          agentEndedCall = false;

          // Emitted here, not after the awaited dataService call below, so the
          // panel switches to its dialing state immediately — and only in the
          // tab that owns the call, since this sits behind the ownership latch.
          emitCallConnectingEvent();

          callProps.setCallId(contactId);
          callProps.awsRumAgent?.recordEvent('callConnecting', {
            patientGuid: callProps.patientGuid,
            phoneNumber: callProps.phoneNumber,
            contactId,
          });

          let callData = {};
          callData = {
            patientGuid: callProps.patientGuid,
            actionFrom: callProps.actionFrom,
          };

          contactToPatientMap.set(contactId, {
            patientGuid: callProps.patientGuid,
            ringStartedAt: new Date(),
          });
          callProps.wsClient.send(SocketRoutes.OnCallStarted, {
            patientGuid: callProps.patientGuid,
            isRpmCall: callProps.isRpmCall,
          });
          callProps.callbacks?.onCallStarted?.(
            contactId,
            callProps.patientGuid,
            callProps.isRpmCall,
          );

          if (callProps.isRpmCall) {
            callData = {
              ...callData,
              deviceId: callProps.deviceId,
              readingId: callProps.readingId,
              type: 'call',
              message: contactId,
              commentType: 'rpm',
            };
          } else {
            callData = {
              ...callData,
              callId: contactId,
            };
          }

          if (!callProps.isReadOnlyRole) {
            const { error } = await dataService.createOne(
              !callProps.isRpmCall
                ? API_CCM_PATIENT_CALL
                : callProps.deviceId && callProps.readingId
                ? API_READINGS_ACTIONS
                : API_PATIENTS_COMMENTS,
              callData,
            );

            if (error) showResult(error);
          }
        });

        contact.onConnected(async (event) => {
          if (event.isInbound()) return;
          const { contactId } = event;
          const currentPatient = contactToPatientMap.get(contactId);
          // Ownership gate, matching onEnded and onDestroy below. The shared
          // worker delivers this event to EVERY tab with a live CCP, so a
          // second open call window would otherwise jump straight from 'idle'
          // to 'connected' on a call it does not own — and never leave, since
          // its onEnded is gated and no callEnded ever arrives. That stranded
          // the agent in a window whose close button only said "end the call
          // first" and whose End call had no contact to act on.
          if (!currentPatient) return;
          currentPatient.connectedAt = new Date();
          emitCallStartedEvent();
        });

        contact.onDestroy(async (event) => {
          if (event.isInbound()) return;
          const { contactId } = event;
          const currentPatientGuid =
            contactToPatientMap.get(contactId)?.patientGuid;
          if (currentPatientGuid) {
            const destroyProps = currentProps;
            destroyProps?.awsRumAgent?.recordEvent('callDestroyed', {
              patientGuid: currentPatientGuid,
              contactId,
            });
            destroyProps?.wsClient.send(SocketRoutes.OnCallEnded, {
              patientGuid: currentPatientGuid,
              isRpmCall: destroyProps?.isRpmCall,
            });
            destroyProps?.callbacks?.onCallEnded?.();
          }
        });

        contact.onEnded(async (event) => {
          if (event.isInbound()) return;

          const { contactId } = event;
          const currentPatient = contactToPatientMap.get(contactId);
          if (currentPatient) {
            const currentPatientGuid = currentPatient.patientGuid;
            const endedProps = currentProps;

            // "Could not connect": the ring ended without the patient ever
            // answering (onConnected never set connectedAt) and not because the
            // agent hung it up. The reason, when Connect reports one, is the
            // agent error state a failed call leaves behind — the same snapshot
            // that moved the contact to ENDED usually carries it, and the
            // panel's agent-state poll backfills the rare case where it lands
            // a beat later.
            const agentState = getAgentState();
            const failure =
              !currentPatient.connectedAt && !agentEndedCall
                ? {
                    reason: isErrorStateFromThisCall(
                      agentState,
                      currentPatient.ringStartedAt,
                    )
                      ? agentState!.name
                      : null,
                  }
                : null;

            endedProps?.awsRumAgent?.recordEvent('callDestroyed', {
              patientGuid: currentPatientGuid,
              contactId,
            });
            if (failure) {
              endedProps?.awsRumAgent?.recordEvent('callNotConnected', {
                patientGuid: currentPatientGuid,
                contactId,
                reason: failure.reason ?? 'unknown',
              });
            }
            endedProps?.wsClient.send(SocketRoutes.OnCallEnded, {
              patientGuid: currentPatientGuid,
              isRpmCall: endedProps?.isRpmCall,
            });
            endedProps?.callbacks?.onCallEnded?.();

            const callDurationSeconds = currentPatient.connectedAt
              ? differenceInSeconds(new Date(), currentPatient.connectedAt)
              : 0;
            emitCallEndedEvent({
              patientGuid: currentPatientGuid,
              callDuration: callDurationSeconds,
              module: endedProps?.isRpmCall ? ModuleType.RPM : ModuleType.CCM,
              callId: contactId,
              failure,
            });
          }
        });
      });
    }
  }
};

// Voice contacts this tab may act on. The shared worker syncs EVERY contact to
// every open tab, so the raw list can contain calls that belong to another
// popup (claimed there via the ownership channel) and inbound contacts the app
// never handles — acting on either from here would be reaching into someone
// else's call.
// contact.isInbound() dereferences getInitialConnection() with no null check
// (streams 2.15), and that connection is absent on a contact that never got one
// — a cleared, errored or missed contact still sitting in the agent's list. A
// throw from inside the filter below would take out the WHOLE list, and since
// every caller treats a throw as "no contacts", one stale contact would disable
// every call control and leave an ENDED contact uncleared (agent stuck in ACW).
// Treated as outbound on failure so the state filters stay in charge: that keeps
// such a contact eligible for the clear() path that releases the agent.
const isInboundContact = (contact: connect.Contact): boolean => {
  try {
    return contact.isInbound();
  } catch {
    return false;
  }
};

const getVoiceContacts = (): connect.Contact[] => {
  if (!ccpInitialized || !cachedAgent) return [];
  return cachedAgent
    .getContacts(connect.ContactType.VOICE)
    .filter(
      (contact) =>
        !isInboundContact(contact) &&
        !contactsClaimedByOtherTab.has(contact.getContactId()),
    );
};

/**
 * Does THIS tab have a voice contact the patient is actually talking on?
 *
 * The authority behind the modal's "end the call before closing" guard. The
 * panel's own phase can outlive the contact — a tab that misses the ENDED event
 * keeps rendering 'connected' — and a guard that trusts the phase alone turns
 * that into a window the agent cannot close by any route. Asking the SDK means
 * a stale phase costs a stale label, never a trapped window.
 */
const hasConnectedVoiceContact = (): boolean => {
  try {
    return getVoiceContacts().some(
      (contact) =>
        contact.getState().type === connect.ContactStateType.CONNECTED,
    );
  } catch {
    return false;
  }
};

// The contact the panel's controls act on: the one being dialed or live. An
// ENDED contact lingering in after-call work must never be picked up here —
// hold/mute/DTMF against it silently target nothing, and End call would clear
// the stale contact while leaving a fresh ring untouched.
const getActiveVoiceContact = (): connect.Contact | undefined =>
  getVoiceContacts().find((contact) => {
    const stateType = contact.getState().type;
    return (
      stateType === connect.ContactStateType.CONNECTING ||
      stateType === connect.ContactStateType.CONNECTED
    );
  });

const endActiveContact = () => {
  try {
    getVoiceContacts().forEach((contact) => {
      const stateType = contact.getState().type;
      if (stateType === connect.ContactStateType.CONNECTING) {
        const agentConnection = contact.getAgentConnection();
        // Nothing to destroy means nothing was abandoned, so the flag must not
        // be set — see hangUpActiveContact for why it is cleared on failure.
        if (!agentConnection) return;
        // The agent chose to abandon the ring, so the ENDED that follows must
        // not be reported as a call that could not connect.
        agentEndedCall = true;
        agentConnection.destroy({
          failure: () => {
            agentEndedCall = false;
          },
        });
      } else if (stateType === connect.ContactStateType.ENDED) {
        // clear(), not destroy() — Contact.destroy() is a deprecated no-op
        // in streams 2.x (it only logs a warning), so it never released the
        // agent from the ended contact.
        contact.clear({});
      }
      // CONNECTED (live call) is left untouched
    });
  } catch {
    // SDK may throw if CCP is not in a connectable state
  }
};

// ── Panel controls ─────────────────────────────────────────────────────────
// The panel drives the call through these instead of through the Amazon CCP's
// own buttons, which now live in a hidden iframe. Each is guarded the same way
// endActiveContact is: the SDK throws if the CCP is not in a connectable state.
//
// Every control takes an onFailure callback and the panel rolls its optimistic
// state back when it fires. A control that fails silently leaves the tile
// claiming something the call is not doing — a muted tile over an open mic.

interface IControlCallbacks {
  onFailure?: (reason?: string) => void;
}

const withActiveContact = (
  fn: (contact: connect.Contact) => void,
  onUnavailable?: () => void,
) => {
  try {
    const contact = getActiveVoiceContact();
    if (contact) fn(contact);
    else onUnavailable?.();
  } catch {
    // SDK may throw if CCP is not in a connectable state
    onUnavailable?.();
  }
};

/**
 * Hang up.
 *
 * endActiveContact() deliberately leaves a CONNECTED contact alone — closing
 * the popup must not drop a live patient call — so the panel's own End call
 * button cannot reuse it. CONNECTING and CONNECTED both end by destroying the
 * agent's connection; an already-ENDED contact is cleared instead (clear(), not
 * the deprecated no-op destroy()).
 */
const hangUpActiveContact = (callbacks?: IControlCallbacks) => {
  try {
    const failure = (reason: string) => callbacks?.onFailure?.(reason);
    const contact = getActiveVoiceContact();
    if (contact) {
      const agentConnection = contact.getAgentConnection();
      if (!agentConnection) {
        // Without a connection destroy() is never called, so NO callback would
        // ever fire — the agent would be left looking at a button that silently
        // did nothing on a call that is still up.
        callbacks?.onFailure?.('no-agent-connection');
        return;
      }
      // The agent chose this end — a ring that dies now is a cancel, not a
      // call that could not connect. Set before the call rather than from a
      // success callback because the ENDED snapshot can reach onEnded first,
      // and onEnded is what reads this flag. Cleared again if Connect rejects
      // the destroy, so a ring that outlives a failed hang-up is still
      // reported as a call that could not connect instead of a cancel.
      agentEndedCall = true;
      agentConnection.destroy({
        failure: (reason) => {
          agentEndedCall = false;
          failure(reason);
        },
      });
      return;
    }
    // The panel can lag the SDK by a tick: the contact may have ENDED between
    // polls, in which case End call means release the agent from it.
    const ended = getVoiceContacts().find(
      (c) => c.getState().type === connect.ContactStateType.ENDED,
    );
    if (ended) {
      ended.clear({ failure });
      return;
    }
    callbacks?.onFailure?.('no-active-contact');
  } catch {
    callbacks?.onFailure?.('no-active-contact');
  }
};

/**
 * Hold means "the patient is on hold": Amazon Connect plays hold music to the
 * connection that is held, so this must hold the patient's (initial) leg — the
 * same leg the CCP's own Hold button acts on and the same leg transfer's
 * customerOnHold reads. Holding the agent's own connection instead leaves the
 * patient hearing everything while the panel claims otherwise.
 */
const holdContact = (callbacks?: IControlCallbacks) =>
  withActiveContact(
    (contact) => {
      const customer = contact.getActiveInitialConnection();
      if (!customer) {
        callbacks?.onFailure?.('no-customer-connection');
        return;
      }
      customer.hold({ failure: (reason) => callbacks?.onFailure?.(reason) });
    },
    () => callbacks?.onFailure?.('no-active-contact'),
  );

const resumeContact = (callbacks?: IControlCallbacks) =>
  withActiveContact(
    (contact) => {
      const customer = contact.getActiveInitialConnection();
      if (!customer) {
        callbacks?.onFailure?.('no-customer-connection');
        return;
      }
      customer.resume({ failure: (reason) => callbacks?.onFailure?.(reason) });
    },
    () => callbacks?.onFailure?.('no-active-contact'),
  );

/**
 * Mute is SERVER-SIDE: muteParticipant() drops the agent's audio inside Amazon
 * Connect itself, so the patient stops hearing the agent no matter which window
 * owns the microphone. agent.mute() is deliberately NOT used — it only
 * broadcasts a request to mute the local media stream, and with the softphone
 * living inside the hidden CCP iframe that request is not reliably honored
 * (see the Chrome storage-partitioning notes in the streams changelog), which
 * showed up as a Mute tile that lit up without muting anything. muteParticipant
 * is also the only mute that works on a multiparty (transfer) call.
 *
 * Connect rejects muteParticipant while the contact is still CONNECTING, so a
 * mute clicked during the ring reaches onFailure. The panel treats that as an
 * armed mute — kept on the tile, re-sent on callStarted — rather than an error.
 */
const setMuted = (muted: boolean, callbacks?: IControlCallbacks) =>
  withActiveContact(
    (contact) => {
      const agentConnection = contact.getAgentConnection() as
        | connect.VoiceConnection
        | undefined;
      if (!agentConnection) {
        callbacks?.onFailure?.('no-agent-connection');
        return;
      }
      const options = {
        failure: (reason: string) => callbacks?.onFailure?.(reason),
      };
      if (muted) agentConnection.muteParticipant(options);
      else agentConnection.unmuteParticipant(options);
    },
    () => callbacks?.onFailure?.('no-active-contact'),
  );

/**
 * DTMF. Must work while the contact is still CONNECTING — carriers and IVR spam
 * gates ask the caller to dial a digit before the call completes, so gating this
 * on a connected contact makes those calls impossible to complete.
 * getAgentConnection() exists from onConnecting onward.
 */
const sendDigits = (digits: string, callbacks?: IControlCallbacks) =>
  withActiveContact(
    (contact) =>
      contact.getAgentConnection()?.sendDigits(digits, {
        failure: (reason) => callbacks?.onFailure?.(reason),
      }),
    () => callbacks?.onFailure?.('no-active-contact'),
  );

// ── Transfer ───────────────────────────────────────────────────────────────
// Restores what the embedded CCP's "Quick connects" did: pull the endpoints on
// the agent's routing profile, add one as a third-party connection, then either
// hand the call over or bring everyone together.

interface ITransferEndpoint {
  /** endpointARN is stable and unique; used as the React key and the lookup. */
  id: string;
  name: string;
  phoneNumber: string;
  type: string;
}

interface ITransferSnapshot {
  name: string;
  /** The third party has answered, as opposed to still ringing. */
  isConnected: boolean;
  isOnHold: boolean;
  /** The patient's own leg. In a three-way call the agent has to know which
   *  side they are actually talking to before they say anything. */
  customerOnHold: boolean;
}

// getEndpoints hands back Endpoint instances that the SDK expects to receive
// back verbatim, so the originals are kept here and the UI is given plain data.
const endpointsById = new Map<string, connect.Endpoint>();

const describeEndpoint = (endpoint: connect.Endpoint): ITransferEndpoint => ({
  id: endpoint.endpointARN || endpoint.phoneNumber || endpoint.name,
  name: endpoint.name || endpoint.phoneNumber || '',
  phoneNumber: endpoint.phoneNumber || '',
  type: endpoint.type || '',
});

const AGENT_WAIT_TIMEOUT_MS = 5000;
const AGENT_WAIT_INTERVAL_MS = 100;

/**
 * Resolves once the cached Agent handle exists, or null if it never turns up.
 *
 * The transfer drawer can mount before AGENT_INIT has fired, and returning an
 * empty list then is permanent for that mount: the drawer fetches once from a
 * useEffect with no retry, so the agent would see no quick connects at all and
 * only the manual dial field.
 *
 * connect.agent(f) is deliberately not used to wait. It subscribes f to the
 * event bus permanently (see cachedAgent at the top of this file), so a drawer
 * opened repeatedly would leak a subscription every time. Polling the cache the
 * one live subscription fills cannot leak and costs nothing.
 */
const waitForAgent = (): Promise<connect.Agent | null> =>
  new Promise((resolve) => {
    if (cachedAgent) {
      resolve(cachedAgent);
      return;
    }
    // No CCP at all: there is nothing to wait for.
    if (!ccpInitialized) {
      resolve(null);
      return;
    }
    const waitStartedAt = Date.now();
    const id = window.setInterval(() => {
      const timedOut = Date.now() - waitStartedAt > AGENT_WAIT_TIMEOUT_MS;
      // terminate() clears both, so a CCP torn down mid-wait ends the wait.
      if (cachedAgent || !ccpInitialized || timedOut) {
        window.clearInterval(id);
        resolve(cachedAgent);
      }
    }, AGENT_WAIT_INTERVAL_MS);
  });

/**
 * Quick connects for this agent's routing profile. Resolves to an empty list
 * rather than rejecting: an instance with no quick connects configured is a
 * valid setup, and the manual dial field still works.
 */
const getTransferEndpoints = async (): Promise<ITransferEndpoint[]> => {
  const agent = await waitForAgent();
  if (!agent) return [];
  return new Promise((resolve) => {
    try {
      const queueARNs = agent.getAllQueueARNs();
      if (!queueARNs?.length) {
        resolve([]);
        return;
      }
      agent.getEndpoints(queueARNs, {
        success: (data) => {
          const list = data?.endpoints ?? [];
          list.forEach((endpoint) => {
            endpointsById.set(describeEndpoint(endpoint).id, endpoint);
          });
          resolve(list.map(describeEndpoint));
        },
        failure: () => resolve([]),
      });
    } catch {
      resolve([]);
    }
  });
};

/**
 * Dial a third party into the live contact. Amazon Connect holds the customer
 * automatically while the third party rings.
 *
 * `endpointId` is an id from getTransferEndpoints; `rawNumber` is a number the
 * agent typed. Only one of the two is used.
 */
const addTransferConnection = (
  target: { endpointId?: string; rawNumber?: string },
  callbacks?: { onSuccess?: () => void; onFailure?: (reason: string) => void },
) => {
  // The transfer field accepts human formatting — spaces, dashes, parens — but
  // Endpoint.byPhoneNumber expects a bare dialable string (E.164 for numbers),
  // so everything that is not a digit, +, * or # is stripped before the SDK
  // sees it. An entry that strips to nothing falls through to no-endpoint.
  const dialable = target.rawNumber?.replace(/[^0-9+*#]/g, '');
  const endpoint = target.endpointId
    ? endpointsById.get(target.endpointId)
    : dialable
    ? connect.Endpoint.byPhoneNumber(dialable)
    : undefined;

  if (!endpoint) {
    callbacks?.onFailure?.('no-endpoint');
    return;
  }

  // onUnavailable matters as much as failure here. getActiveVoiceContact()
  // filters out anything that is not CONNECTING or CONNECTED, so without it a
  // Dial against a contact that has already ended — or one claimed by another
  // tab — runs neither callback and the agent waits for a party that was never
  // called, which is exactly what this control is meant to make impossible.
  withActiveContact(
    (contact) =>
      contact.addConnection(endpoint, {
        success: () => callbacks?.onSuccess?.(),
        failure: (reason) => callbacks?.onFailure?.(reason),
      }),
    () => callbacks?.onFailure?.('no-active-contact'),
  );
};

/** Rotate between the customer and the third party. */
const swapTransferConnections = (callbacks?: IControlCallbacks) =>
  withActiveContact(
    (contact) =>
      contact.toggleActiveConnections({
        failure: (reason) => callbacks?.onFailure?.(reason),
      }),
    () => callbacks?.onFailure?.('no-active-contact'),
  );

/** Put everyone on one call. */
const joinTransferConnections = (callbacks?: IControlCallbacks) =>
  withActiveContact(
    (contact) =>
      contact.conferenceConnections({
        failure: (reason) => callbacks?.onFailure?.(reason),
      }),
    () => callbacks?.onFailure?.('no-active-contact'),
  );

/**
 * Hand the call over: the agent drops out and leaves the customer talking to
 * the third party.
 *
 * The conference comes first on purpose. If the agent simply hangs up while the
 * customer is still on hold, the two are not joined and the customer can be
 * dropped rather than transferred — so the agent connection is only destroyed
 * once Connect confirms everyone is on the same call.
 */
const completeTransfer = (callbacks?: {
  onFailure?: (reason: string) => void;
}) =>
  withActiveContact(
    (contact) => {
      const agentConnection = contact.getAgentConnection();
      const failure = (reason: string) => callbacks?.onFailure?.(reason);
      // After Join all the legs are already conferenced, and Connect rejects a
      // second conferenceConnections — completing then is just dropping the
      // agent leg. The conference-first path stays for the un-joined case.
      const third = contact.getSingleActiveThirdPartyConnection();
      const customer = contact.getActiveInitialConnection();
      const alreadyConferenced =
        !!third &&
        third.getState()?.type === connect.ConnectionStateType.CONNECTED &&
        !third.isOnHold() &&
        !!customer &&
        !customer.isOnHold();
      if (alreadyConferenced) {
        agentConnection?.destroy({ failure });
        return;
      }
      contact.conferenceConnections({
        success: () => agentConnection?.destroy({ failure }),
        failure,
      });
    },
    () => callbacks?.onFailure?.('no-active-contact'),
  );

/**
 * Abandon the transfer and go back to the customer. Amazon Connect put the
 * customer on hold when the third party was dialed, so "going back" is two
 * steps: drop the third-party leg, then take the customer off hold — otherwise
 * the patient is left listening to hold music with no indication on the tiles.
 */
const cancelTransfer = (callbacks?: IControlCallbacks) =>
  withActiveContact(
    (contact) => {
      const resumeCustomer = () => {
        const customer = contact.getActiveInitialConnection();
        if (customer?.isOnHold()) {
          customer.resume({
            failure: (reason) => callbacks?.onFailure?.(reason),
          });
        }
      };
      // The third party may already be gone — they hung up while the customer
      // sat on Amazon's automatic transfer hold — and cancelling then means
      // only taking the customer off the hold music.
      const third = contact.getSingleActiveThirdPartyConnection();
      if (!third) {
        resumeCustomer();
        return;
      }
      third.destroy({
        success: resumeCustomer,
        failure: (reason) => {
          // Even a failed drop must still try to get the patient off hold.
          resumeCustomer();
          callbacks?.onFailure?.(reason);
        },
      });
    },
    () => callbacks?.onFailure?.('no-active-contact'),
  );

/**
 * Read the third party off the live contact. Polled by the panel on the tick it
 * already runs, rather than adding a subscription to the single carefully
 * managed connect.contact() handler above.
 */
const getTransferSnapshot = (): ITransferSnapshot | null => {
  try {
    const contact = getActiveVoiceContact();
    const third = contact?.getSingleActiveThirdPartyConnection();
    if (!contact || !third) return null;
    const state = third.getState()?.type;
    const customerState = contact
      .getActiveInitialConnection()
      ?.getState()?.type;
    return {
      name: third.getEndpoint()?.name || third.getEndpoint()?.phoneNumber || '',
      isConnected: state === connect.ConnectionStateType.CONNECTED,
      isOnHold: state === connect.ConnectionStateType.HOLD,
      customerOnHold: customerState === connect.ConnectionStateType.HOLD,
    };
  } catch {
    return null;
  }
};

interface ICallControlSnapshot {
  /** The patient's leg is on hold (they hear hold music). */
  isCustomerOnHold: boolean;
  /** The agent's audio is muted server-side. */
  isAgentMuted: boolean;
}

/**
 * SDK truth for the two toggle tiles. The tiles flip optimistically on click
 * and roll back on failure; this snapshot, polled on the panel's existing
 * tick, covers state the app did not change itself — Amazon's automatic
 * customer hold while a transfer dials, a supervisor force-mute, a resume
 * performed by joining a conference.
 */
const getCallControlSnapshot = (): ICallControlSnapshot | null => {
  try {
    const contact = getActiveVoiceContact();
    if (!contact) return null;
    const agentConnection = contact.getAgentConnection() as
      | connect.VoiceConnection
      | undefined;
    return {
      isCustomerOnHold:
        contact.getActiveInitialConnection()?.isOnHold() ?? false,
      isAgentMuted: agentConnection?.isMute() ?? false,
    };
  } catch {
    return null;
  }
};

interface IAgentStateSnapshot {
  name: string;
  isRoutable: boolean;
  /** Amazon parked the agent in an error state — the residue a failed call
   *  leaves behind (FailedConnectCustomer, LineEngagedCustomer, …), which is
   *  the closest thing Connect gives to a "why" for a call that never
   *  connected. */
  isErrorState: boolean;
  /** How long the agent has been in this state, in ms. The state itself is
   *  agent-global, so this is what tells a reason belonging to the call being
   *  reported apart from one left over from an earlier dial. null when the SDK
   *  will not give a duration. */
  durationMs: number | null;
}

/**
 * DISPLAY ONLY. Nothing in the app sets agent state and the panel must not offer
 * to — the footer renders this as plain text with no hit target. Making it
 * settable needs the routing-profile state list, setState and error handling.
 *
 * Before the agent has initialised there is no cached instance; this returns
 * null and the footer renders nothing.
 */
const getAgentState = (): IAgentStateSnapshot | null => {
  if (!ccpInitialized || !cachedAgent) return null;
  try {
    const state = cachedAgent.getState();
    if (!state?.name) return null;
    // Read separately: a throw here must cost the duration, not the whole
    // snapshot the footer renders from.
    let durationMs: number | null = null;
    try {
      const duration = cachedAgent.getStateDuration();
      durationMs = Number.isFinite(duration) ? duration : null;
    } catch {
      durationMs = null;
    }
    return {
      name: state.name,
      isRoutable: state.type === connect.AgentStateType.ROUTABLE,
      // The runtime AgentStateType enum only defines init/routable/
      // not_routable/offline — ERROR exists in the .d.ts alone, so
      // connect.AgentStateType.ERROR is undefined at runtime and comparing
      // against it never matches. The snapshot carries the literal 'error'.
      isErrorState: String(state.type) === 'error',
      durationMs,
    };
  } catch {
    return null;
  }
};

/**
 * Is this error state the reason THIS call failed?
 *
 * Agent state is global and, with the Amazon CCP UI hidden in an iframe, the
 * agent never acknowledges an error state — so one left behind by an earlier
 * failed dial is still sitting there when the next call ends. Attributing it
 * blindly makes the panel state a confident, wrong cause ("The patient's line
 * is busy") for a call that failed some other way. Connect must have entered
 * the state at or after this call started ringing, which is what a state
 * duration no longer than the call's own age says.
 */
const isErrorStateFromThisCall = (
  state: IAgentStateSnapshot | null,
  ringStartedAt: Date,
): boolean => {
  if (!state?.isErrorState || state.durationMs === null) return false;
  return state.durationMs <= Date.now() - ringStartedAt.getTime();
};

const terminate = () => {
  connect.core.terminate();
  // Explicitly remove the contact handler so the next init() registers a
  // single fresh one. connect.core.terminate() also unsubscribes it via the
  // event bus, but dropping our own handle keeps the two in sync and prevents
  // a stale handler from ever firing with an outdated patientGuid.
  contactSubscription?.unsubscribe();
  contactSubscription = null;
  agentSubscription?.unsubscribe();
  agentSubscription = null;
  // The Agent instance belongs to the CCP session that is being torn down.
  cachedAgent = null;
  currentProps = null;
  ccpInitialized = false;
  // Close this tab's ownership channel; other tabs keep their own open. The
  // claimed-contact set is per-tab session state and is dropped with it.
  ownershipChannel?.close();
  ownershipChannel = null;
  contactsClaimedByOtherTab.clear();
  // Clear any dial that never connected, so it can't claim a later call.
  hasPendingOutboundDial = false;
  agentEndedCall = false;
  // Endpoint objects belong to the CCP session that is being torn down.
  endpointsById.clear();
  const containerDiv = document.getElementById('connect-el');
  if (containerDiv) {
    const iframe = containerDiv.firstElementChild;
    if (iframe) containerDiv.removeChild(iframe);
  }
};

export {
  init,
  terminate,
  endActiveContact,
  hasConnectedVoiceContact,
  hangUpActiveContact,
  holdContact,
  resumeContact,
  setMuted,
  sendDigits,
  getAgentState,
  getCallControlSnapshot,
  getTransferEndpoints,
  addTransferConnection,
  swapTransferConnections,
  joinTransferConnections,
  completeTransfer,
  cancelTransfer,
  getTransferSnapshot,
};
export type {
  IAgentStateSnapshot,
  ICallControlSnapshot,
  ITransferEndpoint,
  ITransferSnapshot,
};
